import { useEffect, useState } from "react";
import { Card, CardContent, EmptyState, Skeleton } from "@/design-system/components/ui";
import { formatRelativeTime } from "@/design-system/lib/format";
import { brokeragePortalApi, type BrokerageActivityItem } from "../api/brokerage";

// Brokerage / Sub-Broker PR-10 — the Activity tab: a portal-safe "what my
// sub-brokers are doing" feed, unioning the application lifecycle and the
// deal-submission lifecycle across the firm's roster. Attribution is by
// sub-broker; there are no money/commission events (those live in the
// dashboard/ledger, gated by the finance visibility). Portal-local: design-system
// kit only, no React Query.

const CATEGORY_LABEL: Record<string, string> = {
  application: "Application",
  deal: "Deal",
};

function CategoryPill({ category }: { category: string }) {
  return (
    <span className="shrink-0 rounded-full bg-white-warm px-space-2 py-px text-caption uppercase tracking-wide text-gray-mid">
      {CATEGORY_LABEL[category] ?? "Update"}
    </span>
  );
}

export function BrokerageActivityTab() {
  const [items, setItems] = useState<BrokerageActivityItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await brokeragePortalApi.activity();
        if (!cancelled) setItems(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your firm's activity.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p className="text-small text-status-error">{error}</p>;

  if (!items) {
    return (
      <div className="space-y-space-2" data-testid="brokerage-activity-loading">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        title="No activity yet"
        description="As your sub-brokers submit applications and deals, their activity will appear here."
      />
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <ul className="divide-y divide-border-light">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-space-3 px-space-4 py-space-3">
              <CategoryPill category={item.category} />
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-body text-onyx">
                  <span className="font-semibold">{item.subBrokerName}</span>
                  <span className="text-gray-mid"> · {item.title}</span>
                </p>
              </div>
              <span className="shrink-0 text-caption text-gray-mid">
                {formatRelativeTime(item.occurredAt)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
